import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Container,
  Drawer,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { Menu as MenuIcon, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import SidebarCst from "@components/SidebarCst";
import CorporateSelect from "@components/SidebarCst/CorporateSelect";
import { SidebarSection } from "@components/SidebarCst/types";
import {
  SIDEBAR_OPEN_MENU_TEST_PROPS,
  SIDEBAR_TOGGLE_TEST_PROPS,
} from "@components/SidebarCst/testIds";
import { CreatePickerHost } from "@components/CreateOptionPickerCst/host";
import { EnrollmentModalHost } from "@pages/Contents/V2/Corporate/Catalog/Enrollment/host";
import { AssociateInitiativesHost } from "@pages/Contents/V2/Corporate/Catalog/AssociateInitiatives/host";
import { UsersAndGroupsHost } from "@pages/Contents/V2/Corporate/Catalog/UsersAndGroups/host";
import { EditionEnrollmentHost } from "@pages/Contents/V2/Corporate/Catalog/Editions/EditionEnrollment/host";
import { SessionWizardHost } from "@pages/Contents/V2/Corporate/Catalog/Editions/SessionWizard/host";
import ToastHostCst from "@components/ToastHostCst";
import FooterCst from "@components/FooterCst";
import { useNavigationRoutesV2 } from "@hooks/useNavigationRoutesV2";
import { useFetchDataDetail, useFetchDetail } from "@hooks/useFetchDetail";
import { ServicesURL } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import {
  SidebarActiveItemContext,
  SidebarLayoutContext,
} from "@context/sidebarLayout";
import { useAuth } from "@context/auth";
import { RolesDigitedEnum } from "@context/auth/types";
import { canSeeMySpace } from "@utils/permissions";

const COLLAPSED_STORAGE_KEY = "admin-sidebar-collapsed";
const INITIATIVE_STORAGE_KEY = "admin-sidebar-initiative";

const readStorage = (storage: () => Storage, key: string) => {
  try {
    return storage().getItem(key);
  } catch {
    return null;
  }
};

const writeStorage = (storage: () => Storage, key: string, value: string) => {
  try {
    storage().setItem(key, value);
  } catch {
    // storage not available
  }
};

const CorporateDetailV2Layout: React.FC = () => {
  const { t } = useTranslation("sidebar");
  const { id = "" } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down("lg"));
  const isAutoCollapse = useMediaQuery(theme.breakpoints.between("lg", "xl"), {
    noSsr: true,
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const stored = readStorage(() => localStorage, COLLAPSED_STORAGE_KEY);
    return stored !== null ? stored === "true" : isAutoCollapse;
  });
  const isFirstAutoCollapse = useRef(true);

  useEffect(() => {
    if (isFirstAutoCollapse.current) {
      isFirstAutoCollapse.current = false;
      return;
    }
    setSidebarCollapsed(isAutoCollapse);
  }, [isAutoCollapse]);

  useEffect(() => {
    writeStorage(
      () => localStorage,
      COLLAPSED_STORAGE_KEY,
      String(sidebarCollapsed),
    );
  }, [sidebarCollapsed]);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  const expandSidebar = useCallback(() => {
    writeStorage(() => localStorage, COLLAPSED_STORAGE_KEY, "false");
    setSidebarCollapsed(false);
  }, []);

  const { dataDetail } = useFetchDetail(
    ServicesURL.corporate,
    id,
    PagesID.corporate,
  );

  const { dataDetail: initiativesData } = useFetchDataDetail(
    id ? `${ServicesURL.initiative}/${id}/initiatives?status=ACTIVE` : null,
    PagesID.corporate,
  );

  const initiativeStorageKey = `${INITIATIVE_STORAGE_KEY}-${id}`;
  const [selectedInitiative, setSelectedInitiative] = useState(
    () => readStorage(() => sessionStorage, initiativeStorageKey) || "all",
  );

  useEffect(() => {
    writeStorage(
      () => sessionStorage,
      initiativeStorageKey,
      selectedInitiative,
    );
  }, [initiativeStorageKey, selectedInitiative]);

  const initiativeOptions = useMemo(() => {
    const allOption = { label: t("all-initiatives") || "", value: "all" };
    const initiatives = (initiativesData?.output as any[] | undefined) || [];
    return [
      allOption,
      ...initiatives.map((init: any) => ({
        label: init.name || init.title || `${init.id}`,
        value: String(init.id),
      })),
    ];
  }, [initiativesData, t]);

  useEffect(() => {
    if (!initiativesData) return;
    if (!initiativeOptions.some((o) => o.value === selectedInitiative)) {
      setSelectedInitiative("all");
    }
  }, [initiativesData, initiativeOptions, selectedInitiative]);

  const baseUrl = `/v2/corporate/${id}/detail`;
  const legacyBaseUrl = `/corporate/${id}/detail`;

  const resolveTo = useCallback(
    (route: { to?: string; legacyTo?: string }) => {
      if (route.to) return `${baseUrl}/${route.to}`;
      if (!route.legacyTo) return undefined;
      return route.legacyTo.startsWith("/")
        ? route.legacyTo
        : `${legacyBaseUrl}/${route.legacyTo}`;
    },
    [baseUrl, legacyBaseUrl],
  );

  const navV2 = useNavigationRoutesV2(dataDetail, id);

  const activePath = useMemo(() => {
    const candidates = navV2.flatMap((section) =>
      section.items.flatMap((item) => [
        resolveTo(item),
        ...(item.children || []).map((child) => resolveTo(child)),
      ]),
    );
    return candidates
      .filter(
        (path): path is string =>
          !!path &&
          path !== "/" &&
          (location.pathname === path ||
            location.pathname.startsWith(`${path}/`)),
      )
      .sort((a, b) => b.length - a.length)[0];
  }, [navV2, location.pathname, resolveTo]);

  const activeItem = useMemo(() => {
    if (!activePath) return null;
    const match = navV2
      .flatMap((section) =>
        section.items.flatMap((item) => [item, ...(item.children || [])]),
      )
      .find((route) => resolveTo(route) === activePath);
    return match ? { path: activePath, title: match.title } : null;
  }, [navV2, activePath, resolveTo]);

  const isProductOwner =
    !!user?.roles?.includes(RolesDigitedEnum.PRODUCT_OWNER) &&
    user?.organization_id === 1;

  const handleCorporateChange = useCallback(
    (newId: string) => {
      const target =
        activePath ||
        (canSeeMySpace(user?.permissions)
          ? `${baseUrl}/my-space`
          : legacyBaseUrl);
      navigate(
        target.replace(
          /^(\/v2)?\/corporate\/[^/]+\/detail/,
          (_match, v2) => `${v2 || ""}/corporate/${newId}/detail`,
        ),
      );
    },
    [activePath, baseUrl, legacyBaseUrl, navigate, user?.permissions],
  );

  const corporateSelector = isProductOwner ? (
    <CorporateSelect
      corporateId={id}
      corporateName={dataDetail?.name || "Corporate"}
      searchPlaceholder={t("corporate-search-placeholder") || ""}
      emptyLabel={t("corporate-search-empty") || ""}
      onSelect={handleCorporateChange}
    />
  ) : undefined;

  const sidebarSections: SidebarSection[] = useMemo(
    () =>
      navV2.map((section) => ({
        label: section.label,
        testKey: section.key,
        type: section.type,
        items: section.items
          .filter((i) => i.kind === "card")
          .map((item) => ({
            icon: item.icon,
            title: item.title,
            description: item.description,
            to: resolveTo(item),
            active: !!activePath && resolveTo(item) === activePath,
            testKey: item.key,
            actionButton: item.actionButton,
            children: item.children?.map((c) => ({
              title: c.title,
              to: resolveTo(c),
              active: !!activePath && resolveTo(c) === activePath,
              testKey: `${item.key}-${c.key}`,
            })),
          })),
        simpleItems: section.items
          .filter((i) => i.kind === "simpleItem")
          .map((item) => ({
            icon: item.icon,
            title: item.title,
            description: item.description,
            to: resolveTo(item),
            active: !!activePath && resolveTo(item) === activePath,
            testKey: item.key,
            hasChevron: item.hasChevron,
            children: item.children?.map((c) => ({
              title: c.title,
              to: resolveTo(c),
              active: !!activePath && resolveTo(c) === activePath,
              testKey: `${item.key}-${c.key}`,
              nested: c.nested,
            })),
          })),
      })),
    [navV2, activePath, resolveTo],
  );

  const desktopSidebar = (
    <SidebarCst
      header={{
        corporateName: dataDetail?.name || "Corporate",
        corporateSelector,
        initiativeLabel: t("initiative-label") || "",
        initiativeOptions,
        selectedInitiative,
        onInitiativeChange: setSelectedInitiative,
      }}
      sections={sidebarSections}
      collapsed={sidebarCollapsed}
      onExpandRequest={expandSidebar}
    />
  );

  // Drawer (mobile) always renders the expanded version.
  const drawerSidebar = (
    <SidebarCst
      fullHeight
      header={{
        corporateName: dataDetail?.name || "Corporate",
        corporateSelector,
        initiativeLabel: t("initiative-label") || "",
        initiativeOptions,
        selectedInitiative,
        onInitiativeChange: setSelectedInitiative,
      }}
      sections={sidebarSections}
    />
  );

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        gap: "16px",
        padding: "16px 16px 0",
        backgroundColor: "background.default",
      }}
    >
      {!isCompact && (
        <Box sx={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
          {desktopSidebar}
          <IconButton
            {...SIDEBAR_TOGGLE_TEST_PROPS}
            onClick={() => setSidebarCollapsed((v) => !v)}
            aria-label={
              sidebarCollapsed ? "expand sidebar" : "collapse sidebar"
            }
            sx={{
              position: "sticky",
              top: "calc(var(--app-header-height, 180px) + 32px)",
              width: "40px",
              height: "40px",
              marginTop: "16px",
              borderRadius: "10px",
              backgroundColor: theme.palette.background.default,
              border: `1px solid ${theme.palette.divider}`,
              color: theme.palette.text.primary,
              flexShrink: 0,
              transition:
                "top 700ms ease, background-color 0.15s ease, border-color 0.15s ease",
              "&:hover": {
                backgroundColor: theme.palette.action.hover,
                borderColor: theme.palette.text.tertiary,
              },
            }}
          >
            {sidebarCollapsed ? (
              <PanelLeftOpen size={20} />
            ) : (
              <PanelLeftClose size={20} />
            )}
          </IconButton>
        </Box>
      )}

      {isCompact && (
        <Drawer
          anchor="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          PaperProps={{
            sx: {
              backgroundColor: "transparent",
              border: "none",
              boxShadow: "none",
              padding: "16px",
            },
          }}
        >
          {drawerSidebar}
        </Drawer>
      )}

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {isCompact && (
          <Box sx={{ mb: 2 }}>
            <IconButton
              {...SIDEBAR_OPEN_MENU_TEST_PROPS}
              onClick={() => setDrawerOpen(true)}
              aria-label="open menu"
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: "8px",
                color: theme.palette.text.primary,
              }}
            >
              <MenuIcon size={20} />
            </IconButton>
          </Box>
        )}
        <Container maxWidth={"xxl"} sx={{ px: { xs: 0, md: 0 }, flex: 1 }}>
          <SidebarLayoutContext.Provider value={true}>
            <SidebarActiveItemContext.Provider value={activeItem}>
              <Outlet
                context={{
                  selectedInitiative,
                  setSelectedInitiative,

                  selectedInitiativeName:
                    initiativeOptions.find(
                      (o) => o.value === selectedInitiative,
                    )?.label || "",
                }}
              />
            </SidebarActiveItemContext.Provider>
          </SidebarLayoutContext.Provider>
        </Container>
        <Box sx={{ mt: 4 }}>
          <FooterCst />
        </Box>
      </Box>

      <CreatePickerHost />
      <EnrollmentModalHost />
      <AssociateInitiativesHost />
      <UsersAndGroupsHost />
      <EditionEnrollmentHost />
      <SessionWizardHost />
      <ToastHostCst />
    </Box>
  );
};

export default CorporateDetailV2Layout;
